/**
 * @fileoverview ClubOS Contracts page — full contract management with Add/Edit dialog
 * FC Metropolitan ISL Season 2025/26 — all values in INR.
 * TODO: Replace mock data with API calls: financeService.getContracts(), financeService.saveContract()
 */

import { useState, useMemo, useEffect, useCallback } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";

// ClubOS components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";

// ClubOS examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

// ClubOS utilities & data
import { formatCompact, formatDateIN, CLUB_NAME } from "utils/indiaLocale";
import { PermissionGate } from "components/PermissionGate";
import { financeService } from "api";
import FootballLoader from "components/FootballLoader";
import apiClient from "api/client";
import { useAuth } from "context/AuthContext";

// ── API → UI shape mapper ────────────────────────────────────────────────────
function mapApiContract(dto) {
  return {
    id:            dto.id,
    playerId:      dto.playerId,
    player:        dto.playerName,
    position:      dto.position || "",
    type:          "Standard",
    monthlySalary: dto.monthlySalary,
    annualValue:   dto.monthlySalary * 12,
    contractStart: dto.startDate,
    contractEnd:   dto.endDate,
    status:        dto.status,
    agentName:     dto.agentName || "",
    agentFee:      dto.agentFeeAmount || 0,
    joiningBonus:  0,
    notes:         "",
    currency:      dto.currency || "INR",
  };
}

// ─── CONSTANTS ─────────────────────────────────────────────────────────────────

/** Status badge colour mapping */
const STATUS_COLORS = {
  Active:           "success",
  Expiring:         "error",
  "In Negotiations":"warning",
  Expired:          "dark",
  "Pending Signature": "info",
};

/** Position options for dropdown */
const POSITIONS = ["GK", "CB", "LB", "RB", "CDM", "CM", "CAM", "LW", "RW", "ST"];

/** Contract type options */
const CONTRACT_TYPES = ["Standard", "Loan", "Youth Academy", "Trial"];

/** Contract status options */
const CONTRACT_STATUSES = ["Active", "Expiring", "In Negotiations", "Pending Signature", "Expired"];

/** Empty form for Add dialog */
const EMPTY_FORM = {
  playerId: "",
  player: "",
  position: "",
  type: "Standard",
  monthlySalary: "",
  joiningBonus: "",
  performanceBonus: "",
  contractStart: "",
  contractEnd: "",
  extensionClause: "",
  agentFee: "",
  agentName: "",
  status: "Active",
  notes: "",
};

/** Calculate days remaining until a date */
function daysRemaining(dateStr) {
  if (!dateStr) return null;
  const diff = new Date(dateStr) - new Date();
  return Math.round(diff / (1000 * 60 * 60 * 24));
}

/** Days remaining colour coding */
function daysColor(days) {
  if (days === null) return "text";
  if (days < 0) return "error";
  if (days < 90) return "error";
  if (days < 180) return "warning";
  return "success";
}

/** Validate form */
function validateForm(form, isEdit) {
  const errors = {};
  if (!isEdit && !form.playerId) errors.player = "Player is required";
  if (!form.monthlySalary || isNaN(Number(form.monthlySalary))) errors.monthlySalary = "Valid monthly salary required";
  if (!form.contractStart) errors.contractStart = "Start date is required";
  if (!form.contractEnd) errors.contractEnd = "End date is required";
  return errors;
}

// ─── COMPONENT ─────────────────────────────────────────────────────────────────

function Contracts() {
  const { currentUser } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [players, setPlayers] = useState([]);    // for player picker

  const fetchContracts = useCallback(() => {
    setLoading(true);
    financeService.getContracts()
      .then((data) => setContracts(data.map(mapApiContract)))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchContracts();
    // Load players for the Add Contract player dropdown
    apiClient.get("/players", { params: { pageSize: 200 } })
      .then((res) => setPlayers(res.items ?? res ?? []))
      .catch(() => {});
  }, [fetchContracts]);

  // Filters
  const [search, setSearch] = useState("");
  const [tabValue, setTabValue] = useState(0); // 0=All, 1=Active, 2=Expiring, 3=Negotiations, 4=Expired
  const TAB_STATUSES = ["All", "Active", "Expiring", "In Negotiations", "Expired"];

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});

  // View details dialog
  const [viewContract, setViewContract] = useState(null);

  // ── Filtered contracts ────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    return contracts.filter((c) => {
      const matchSearch = c.player.toLowerCase().includes(search.toLowerCase());
      const tabStatus = TAB_STATUSES[tabValue];
      const matchTab = tabStatus === "All" || c.status === tabStatus;
      return matchSearch && matchTab;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contracts, search, tabValue]);

  // ── KPIs ─────────────────────────────────────────────────────────────────
  const totalContracts = contracts.length;
  const expiringCount = contracts.filter((c) => c.status === "Expiring").length;
  const totalMonthlyCommitment = contracts.reduce((sum, c) => sum + (c.monthlySalary || 0), 0);
  const avgContractMonths = contracts.reduce((sum, c) => {
    const start = new Date(c.contractStart);
    const end = new Date(c.contractEnd);
    return sum + Math.round((end - start) / (1000 * 60 * 60 * 24 * 30));
  }, 0) / (contracts.length || 1);

  // ── Dialog handlers ───────────────────────────────────────────────────────
  const openAddDialog = () => {
    setEditingContract(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setDialogOpen(true);
  };

  const openEditDialog = (contract) => {
    setEditingContract(contract);
    setForm({
      playerId: contract.playerId || "",
      player: contract.player,
      position: contract.position,
      type: contract.type,
      monthlySalary: String(contract.monthlySalary || ""),
      joiningBonus: String(contract.joiningBonus || ""),
      performanceBonus: contract.performanceBonus || "",
      contractStart: contract.contractStart || "",
      contractEnd: contract.contractEnd || "",
      extensionClause: String(contract.extensionClause || ""),
      agentFee: String(contract.agentFee || ""),
      agentName: contract.agentName || "",
      status: contract.status,
      notes: contract.notes || "",
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSave = async () => {
    const errors = validateForm(form, !!editingContract);
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }

    setLoading(true);
    try {
      const weeklyWage = Number(form.monthlySalary) / 4.33;
      if (editingContract) {
        await financeService.updateContract(editingContract.id, {
          contractId:      editingContract.id,
          startDate:       form.contractStart,
          endDate:         form.contractEnd,
          weeklyWageAmount: weeklyWage,
          currency:        editingContract.currency || "INR",
          agentFeeAmount:  Number(form.agentFee) || null,
          agentName:       form.agentName || null,
          tenantId:        currentUser.tenantId,
        });
      } else {
        await financeService.createContract({
          playerId:         form.playerId,
          startDate:        form.contractStart,
          endDate:          form.contractEnd,
          weeklyWageAmount: weeklyWage,
          currency:         "INR",
          signingBonusAmount: Number(form.joiningBonus) || null,
          agentFeeAmount:   Number(form.agentFee) || null,
          agentName:        form.agentName || null,
          tenantId:         currentUser.tenantId,
        });
      }
      await fetchContracts();
      setDialogOpen(false);
    } catch (err) {
      setFormErrors({ submit: err?.title ?? err?.message ?? "Failed to save contract" });
    } finally {
      setLoading(false);
    }
  };

  const handleTerminate = async (contractId) => {
    if (!window.confirm("Terminate this contract? This cannot be undone.")) return;
    try {
      await financeService.terminateContract(contractId);
      setContracts((prev) => prev.filter((c) => c.id !== contractId));
    } catch (err) {
      alert(err?.title ?? err?.message ?? "Failed to terminate contract");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        {/* ── Page header ─────────────────────────────────────────────────── */}
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <SoftBox>
            <SoftTypography variant="h4" fontWeight="bold" color="dark">
              Contract Management
            </SoftTypography>
            <SoftTypography variant="body2" color="text">
              Player contracts — {CLUB_NAME} · ISL Season 2025/26
            </SoftTypography>
          </SoftBox>
          <PermissionGate permission="finance.edit">
            <SoftButton variant="gradient" color="info" onClick={openAddDialog}>
              <Icon sx={{ mr: 0.5 }}>add</Icon>
              Add Contract
            </SoftButton>
          </PermissionGate>
        </SoftBox>

        {/* ── Summary KPIs ────────────────────────────────────────────────── */}
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Total Contracts" }}
                count={String(totalContracts)}
                percentage={{ color: "info", text: "all players" }}
                icon={{ color: "info", component: "description" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Expiring (≤6 months)" }}
                count={String(expiringCount)}
                percentage={{ color: "error", text: "immediate attention" }}
                icon={{ color: "error", component: "schedule" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Monthly Commitment" }}
                count={formatCompact(totalMonthlyCommitment)}
                percentage={{ color: "warning", text: "total wage bill" }}
                icon={{ color: "warning", component: "payments" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Avg Contract Length" }}
                count={`${Math.round(avgContractMonths)} mo`}
                percentage={{ color: "success", text: "across all contracts" }}
                icon={{ color: "success", component: "calendar_today" }}
              />
            </Grid>
          </Grid>
        </SoftBox>

        {/* ── Table Card ──────────────────────────────────────────────────── */}
        <Card>
          <SoftBox p={3}>
            {/* Search + Tab filters */}
            <Grid container spacing={2} mb={2} alignItems="center">
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  placeholder="Search player name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: <Icon sx={{ mr: 1, color: "#aaa", fontSize: "1.1rem" }}>search</Icon>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={7}>
                <Tabs
                  value={tabValue}
                  onChange={(_, v) => setTabValue(v)}
                  sx={{ "& .MuiTab-root": { fontSize: "0.72rem", minWidth: 90 } }}
                >
                  {TAB_STATUSES.map((s) => (
                    <Tab key={s} label={s} />
                  ))}
                </Tabs>
              </Grid>
            </Grid>

            <SoftTypography variant="caption" color="text" display="block" mb={1}>
              Showing {filtered.length} of {totalContracts} contracts
            </SoftTypography>

            {/* Table */}
            <SoftBox sx={{ overflowX: "auto" }}>
              <SoftBox
                component="table"
                sx={{ width: "100%", borderCollapse: "collapse", minWidth: 850 }}
              >
                <SoftBox component="thead" sx={{ backgroundColor: "#f8f9fa" }}>
                  <SoftBox component="tr">
                    {["Player", "Position", "Type", "Monthly Salary", "Annual Value", "Contract End", "Days Left", "Status", "Actions", ""].map((h) => (
                      <SoftBox
                        key={h}
                        component="th"
                        sx={{
                          textAlign: "left",
                          p: 1.5,
                          color: "text.secondary",
                          fontWeight: 700,
                          fontSize: "0.7rem",
                          textTransform: "uppercase",
                          borderBottom: "2px solid #e9ecef",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {h}
                      </SoftBox>
                    ))}
                  </SoftBox>
                </SoftBox>
                <SoftBox component="tbody">
                  {loading ? (
                    <SoftBox component="tr">
                      <SoftBox component="td" colSpan={9}><FootballLoader label="Loading contracts…" /></SoftBox>
                    </SoftBox>
                  ) : filtered.length === 0 ? (
                    <SoftBox component="tr">
                      <SoftBox component="td" colSpan={9} sx={{ textAlign: "center", p: 4 }}>
                        <Icon sx={{ fontSize: "2rem", color: "#ccc", display: "block", mx: "auto", mb: 1 }}>
                          search_off
                        </Icon>
                        <SoftTypography variant="caption" color="text">
                          No contracts found matching your filters.
                        </SoftTypography>
                      </SoftBox>
                    </SoftBox>
                  ) : (
                    filtered.map((c, i) => {
                      const days = daysRemaining(c.contractEnd);
                      const dayCol = daysColor(days);
                      return (
                        <SoftBox
                          key={c.id}
                          component="tr"
                          sx={{
                            "&:hover": { backgroundColor: "#f8f9fa" },
                            backgroundColor: i % 2 === 0 ? "white" : "#fdfdfd",
                          }}
                        >
                          <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                            <SoftTypography
                              variant="button"
                              fontWeight="medium"
                              color="info"
                              sx={{ cursor: "pointer", textDecoration: "underline" }}
                              onClick={() => setViewContract(c)}
                            >
                              {c.player}
                            </SoftTypography>
                          </SoftBox>
                          <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                            <SoftBadge badgeContent={c.position} color="info" size="xs" container />
                          </SoftBox>
                          <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                            <SoftTypography variant="caption" color="text">{c.type}</SoftTypography>
                          </SoftBox>
                          {/* Monthly Salary — gated on finance.salary.view */}
                          <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                            <PermissionGate
                              permission="finance.salary.view"
                              fallback={
                                <Tooltip title="Finance access required">
                                  <SoftTypography variant="button" fontWeight="bold" color="secondary">
                                    ₹ ***
                                  </SoftTypography>
                                </Tooltip>
                              }
                            >
                              <SoftTypography variant="button" fontWeight="bold" color="dark">
                                {formatCompact(c.monthlySalary)}
                              </SoftTypography>
                            </PermissionGate>
                          </SoftBox>
                          {/* Annual Value */}
                          <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                            <PermissionGate
                              permission="finance.salary.view"
                              fallback={
                                <SoftTypography variant="caption" color="secondary">₹ ***</SoftTypography>
                              }
                            >
                              <SoftTypography variant="caption" color="text">
                                {formatCompact(c.annualValue || c.monthlySalary * 12)}
                              </SoftTypography>
                            </PermissionGate>
                          </SoftBox>
                          <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                            <SoftTypography variant="caption" color="text">
                              {formatDateIN(c.contractEnd)}
                            </SoftTypography>
                          </SoftBox>
                          <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                            <SoftTypography variant="caption" color={dayCol} fontWeight="medium">
                              {days === null ? "—" : days < 0 ? "Expired" : `${days}d`}
                            </SoftTypography>
                          </SoftBox>
                          <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                            <SoftBadge
                              badgeContent={c.status}
                              color={STATUS_COLORS[c.status] || "info"}
                              size="xs"
                              container
                            />
                          </SoftBox>
                          <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                            <SoftBox display="flex" gap={0.75} alignItems="center">
                              <Tooltip title="View full details">
                                <SoftButton
                                  variant="text"
                                  color="info"
                                  size="small"
                                  onClick={() => setViewContract(c)}
                                  sx={{ minWidth: 0, p: "4px 8px" }}
                                >
                                  <Icon sx={{ fontSize: "1rem" }}>visibility</Icon>
                                </SoftButton>
                              </Tooltip>
                              <PermissionGate permission="finance.edit">
                                <SoftButton
                                  variant="outlined"
                                  color="info"
                                  size="small"
                                  onClick={() => openEditDialog(c)}
                                >
                                  Edit
                                </SoftButton>
                                <SoftButton
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  onClick={() => handleTerminate(c.id)}
                                >
                                  Terminate
                                </SoftButton>
                              </PermissionGate>
                            </SoftBox>
                          </SoftBox>
                        </SoftBox>
                      );
                    })
                  )}
                </SoftBox>
              </SoftBox>
            </SoftBox>
          </SoftBox>
        </Card>
      </SoftBox>

      <Footer company={{ href: "#", name: CLUB_NAME }} links={[]} />

      {/* ── View Contract Details Dialog ─────────────────────────────────────── */}
      <Dialog open={!!viewContract} onClose={() => setViewContract(null)} maxWidth="sm" fullWidth>
        {viewContract && (
          <>
            <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", display: "flex", alignItems: "center", gap: 1 }}>
              <Icon sx={{ color: "#17c1e8" }}>description</Icon>
              {viewContract.player} — Contract Details
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2} pt={0.5}>
                {[
                  { label: "Position",      value: viewContract.position },
                  { label: "Contract Type", value: viewContract.type },
                  { label: "Status",        value: viewContract.status },
                  { label: "Contract Start",value: viewContract.contractStart },
                  { label: "Contract End",  value: viewContract.contractEnd },
                  { label: "Days Remaining",value: (() => { const d = Math.round((new Date(viewContract.contractEnd) - new Date()) / 86400000); return d < 0 ? "Expired" : `${d} days`; })() },
                  { label: "Monthly Salary",value: formatCompact(viewContract.monthlySalary) },
                  { label: "Annual Value",  value: formatCompact(viewContract.annualValue || viewContract.monthlySalary * 12) },
                  { label: "Joining Bonus", value: viewContract.joiningBonus ? formatCompact(viewContract.joiningBonus) : "—" },
                  { label: "Agent Fee",     value: viewContract.agentFee ? formatCompact(viewContract.agentFee) : "—" },
                  { label: "Ext. Clause",   value: viewContract.extensionClause ? `${viewContract.extensionClause} months` : "None" },
                ].map(({ label, value }) => (
                  <Grid item xs={6} key={label}>
                    <SoftTypography variant="caption" color="text" fontWeight="bold" display="block"
                      sx={{ textTransform: "uppercase", fontSize: "0.65rem", mb: 0.25 }}>
                      {label}
                    </SoftTypography>
                    <SoftTypography variant="button" fontWeight="medium">{value}</SoftTypography>
                  </Grid>
                ))}
                {viewContract.notes && (
                  <Grid item xs={12}>
                    <SoftTypography variant="caption" color="text" fontWeight="bold" display="block"
                      sx={{ textTransform: "uppercase", fontSize: "0.65rem", mb: 0.25 }}>
                      Notes
                    </SoftTypography>
                    <SoftTypography variant="caption" color="text">{viewContract.notes}</SoftTypography>
                  </Grid>
                )}
                {viewContract.performanceBonus && (
                  <Grid item xs={12}>
                    <SoftTypography variant="caption" color="text" fontWeight="bold" display="block"
                      sx={{ textTransform: "uppercase", fontSize: "0.65rem", mb: 0.25 }}>
                      Performance Bonus Clause
                    </SoftTypography>
                    <SoftTypography variant="caption" color="text">{viewContract.performanceBonus}</SoftTypography>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
              <SoftButton variant="outlined" color="secondary" onClick={() => setViewContract(null)}>
                Close
              </SoftButton>
              <PermissionGate permission="finance.edit">
                <SoftButton variant="gradient" color="info" onClick={() => { openEditDialog(viewContract); setViewContract(null); }}>
                  Edit Contract
                </SoftButton>
              </PermissionGate>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── Add / Edit Contract Dialog ───────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem" }}>
          {editingContract ? `Edit Contract — ${editingContract.player}` : "Add New Contract"}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} pt={1}>
            {/* Player picker / name */}
            <Grid item xs={12} sm={6}>
              {editingContract ? (
                <TextField
                  fullWidth
                  label="Player Name"
                  value={form.player}
                  disabled
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              ) : (
                <FormControl fullWidth size="small" error={!!formErrors.player} required>
                  <InputLabel shrink>Player</InputLabel>
                  <Select
                    value={form.playerId}
                    label="Player"
                    onChange={(e) => {
                      const p = players.find((pl) => pl.id === e.target.value);
                      handleFormChange("playerId", e.target.value);
                      if (p) handleFormChange("position", p.position || "");
                    }}
                  >
                    {players.map((p) => (
                      <MenuItem key={p.id} value={p.id}>{p.name ?? p.fullName}</MenuItem>
                    ))}
                  </Select>
                  {formErrors.player && <FormHelperText>{formErrors.player}</FormHelperText>}
                </FormControl>
              )}
            </Grid>

            {/* Position */}
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small" required error={!!formErrors.position}>
                <InputLabel shrink>Position</InputLabel>
                <Select value={form.position} label="Position" onChange={(e) => handleFormChange("position", e.target.value)}>
                  {POSITIONS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </Select>
                {formErrors.position && <FormHelperText>{formErrors.position}</FormHelperText>}
              </FormControl>
            </Grid>

            {/* Contract Type */}
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Contract Type</InputLabel>
                <Select value={form.type} label="Contract Type" onChange={(e) => handleFormChange("type", e.target.value)}>
                  {CONTRACT_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            {/* Monthly Basic Salary */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Monthly Basic Salary (₹)"
                required
                type="number"
                value={form.monthlySalary}
                onChange={(e) => handleFormChange("monthlySalary", e.target.value)}
                error={!!formErrors.monthlySalary}
                helperText={formErrors.monthlySalary || (form.monthlySalary ? formatCompact(Number(form.monthlySalary)) : "")}
                size="small"
                inputProps={{ min: 0 }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Joining Bonus */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Joining Bonus (₹)"
                type="number"
                value={form.joiningBonus}
                onChange={(e) => handleFormChange("joiningBonus", e.target.value)}
                size="small"
                inputProps={{ min: 0 }}
                helperText={form.joiningBonus ? formatCompact(Number(form.joiningBonus)) : ""}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Agent Fee */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Agent Fee (₹)"
                type="number"
                value={form.agentFee}
                onChange={(e) => handleFormChange("agentFee", e.target.value)}
                size="small"
                inputProps={{ min: 0 }}
                helperText={form.agentFee ? formatCompact(Number(form.agentFee)) : ""}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Performance Bonus */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Performance Bonus Clause"
                placeholder="e.g. ₹50L on 20 ISL starts, ₹25L on India national team call"
                value={form.performanceBonus}
                onChange={(e) => handleFormChange("performanceBonus", e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Contract Start */}
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="Contract Start"
                required
                type="date"
                value={form.contractStart}
                onChange={(e) => handleFormChange("contractStart", e.target.value)}
                error={!!formErrors.contractStart}
                helperText={formErrors.contractStart}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Contract End */}
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="Contract End"
                required
                type="date"
                value={form.contractEnd}
                onChange={(e) => handleFormChange("contractEnd", e.target.value)}
                error={!!formErrors.contractEnd}
                helperText={formErrors.contractEnd}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Extension Clause */}
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="Extension Clause (months)"
                type="number"
                value={form.extensionClause}
                onChange={(e) => handleFormChange("extensionClause", e.target.value)}
                size="small"
                inputProps={{ min: 0, max: 24 }}
                placeholder="e.g. 12"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Status */}
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Status</InputLabel>
                <Select value={form.status} label="Status" onChange={(e) => handleFormChange("status", e.target.value)}>
                  {CONTRACT_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                value={form.notes}
                onChange={(e) => handleFormChange("notes", e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        {formErrors.submit && (
          <SoftBox px={2} pb={1}>
            <SoftTypography variant="caption" color="error">{formErrors.submit}</SoftTypography>
          </SoftBox>
        )}
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <SoftButton variant="outlined" color="secondary" onClick={() => setDialogOpen(false)}>
            Cancel
          </SoftButton>
          <SoftButton variant="gradient" color="info" onClick={handleSave} disabled={loading}>
            {loading ? "Saving…" : editingContract ? "Save Changes" : "Create Contract"}
          </SoftButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Contracts;
